import {
  getAllImages,
  getFeaturedImage,
  hasVirtualViewsElement,
  videoElementWithoutYoutube,
} from "../../SectionFeatured.tsx/utils";
import {
  extractFloorPlanRoomTypes,
  hasFloorPlanData,
} from "../../FloorPlansSection/utils/floorPlanHelpers";

export type OverviewTileKind = "photo" | "360" | "video";
export type OverviewTileType = "featured" | "exterior" | "amenities" | "photo" | "360" | "video";
export type OverviewLayoutCase = "1" | "2" | "3" | "4" | "5+";
export type OverviewGalleryTab = "all" | "3D" | "videos";

export interface OverviewTile {
  position: number; // 1 = hero, then FR-4 order
  kind: OverviewTileKind;
  tileType: OverviewTileType;
  image?: any; // the photo, or the 360° backdrop
  video?: any; // kind === "video"
  tab: OverviewGalleryTab;
}

export interface OverviewGalleryCounts {
  photos: number;
  videos: number;
  views3d: number;
  floorPlans: number;
  hasFloorPlans: boolean;
}

export interface OverviewGalleryModel {
  layoutCase: OverviewLayoutCase;
  mediaCount: number;
  tiles: OverviewTile[];
  counts: OverviewGalleryCounts;
}

const LAYOUT_CASES: OverviewLayoutCase[] = ["1", "2", "3", "4", "5+"];

const collect = (data: any, key: "videos" | "virtual_views") => [
  ...(data?.[key] || []),
  ...(data?.children_short || []).flatMap((child: any) => child?.[key] || []),
];

// Today's SectionFeaturedDesktop choice: the longest `vo_tour`, else the first non-YouTube video.
// No non-YouTube video → no video tile, as today.
const pickVideo = (data: any) => {
  const nonYoutube = videoElementWithoutYoutube(data);
  if (!nonYoutube?.length) return null;
  const voTours = (data?.videos || [])
    .filter((video: any) => video?.type === "vo_tour")
    .sort((v1: any, v2: any) => parseFloat(v2?.duration) - parseFloat(v1?.duration));
  return voTours[0] || nonYoutube[0];
};

const getCounts = (data: any, roomTypesData: any[], photos: any[]): OverviewGalleryCounts => {
  const hasFloorPlans = Boolean(
    hasFloorPlanData(data) ||
      roomTypesData?.some((room: any) =>
        room?.images?.some((img: any) => img?.type === "floor_plan"),
      ),
  );
  return {
    photos: photos.length,
    videos: collect(data, "videos").length,
    views3d: collect(data, "virtual_views").length,
    floorPlans: hasFloorPlans ? extractFloorPlanRoomTypes(data?.children_short).length : 0,
    hasFloorPlans,
  };
};

/**
 * Builds the hero gallery: the featured image as the hero, then exterior · amenities · 360° tour ·
 * video. A slot whose media is missing takes the next photo in API order not already shown; the
 * layout case follows the total media count, so 5+ items always fill all five tiles.
 */
const buildOverviewGalleryTiles = (data: any, roomTypesData: any[] = []): OverviewGalleryModel => {
  const photos: any[] = getAllImages(data) || [];
  const counts = getCounts(data, roomTypesData, photos);
  const hero = getFeaturedImage(data?.images) || photos[0];

  if (!hero) {
    return { layoutCase: "1", mediaCount: 0, tiles: [], counts };
  }

  const isHero = (image: any) =>
    image === hero || Boolean(hero.base_path && image?.base_path === hero.base_path);
  const pool = photos.filter((image) => !isHero(image));
  const take = (predicate: (image: any) => boolean) => {
    const index = pool.findIndex(predicate);
    return index === -1 ? undefined : pool.splice(index, 1)[0];
  };
  const next = () => pool.shift();

  const has360 = Boolean(hasVirtualViewsElement(data));
  const video = pickVideo(data);
  const mediaCount = photos.length + (has360 ? 1 : 0) + (video ? 1 : 0);

  // Tagged photos are taken out of the pool first, so the next-photo fallback never repeats them.
  const exterior = take((image) => image?.type === "exterior");
  const amenities =
    take((image) => image?.type === "amenities") || take((image) => image?.type === "common_area");

  const slots: Array<Omit<OverviewTile, "position"> | undefined> = [
    { kind: "photo", tileType: "featured", image: hero, tab: "all" },
  ];
  const photoSlot = (tagged: any, tileType: OverviewTileType) => {
    const image = tagged || next();
    return image
      ? {
          kind: "photo" as const,
          tileType: tagged ? tileType : ("photo" as const),
          image,
          tab: "all" as const,
        }
      : undefined;
  };
  slots.push(photoSlot(exterior, "exterior"));
  slots.push(photoSlot(amenities, "amenities"));
  slots.push(has360 ? { kind: "360", tileType: "360", tab: "3D" } : photoSlot(undefined, "photo"));
  slots.push(
    video
      ? { kind: "video", tileType: "video", video, tab: "videos" }
      : photoSlot(undefined, "photo"),
  );

  const tiles: OverviewTile[] = slots
    .filter((slot): slot is Omit<OverviewTile, "position"> => Boolean(slot))
    .map((slot, index) => ({ ...slot, position: index + 1 }));

  // The 360° tile shows a photo under the 3D icon, as today's 3D tile does.
  const tile360 = tiles.find((tile) => tile.kind === "360");
  if (tile360) tile360.image = next() || hero;

  return {
    // At most five slots are built (hero + four), so tiles.length is 1–5 here.
    layoutCase: LAYOUT_CASES[tiles.length - 1],
    mediaCount,
    tiles,
    counts,
  };
};

export default buildOverviewGalleryTiles;
